import { createFileRoute } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { formatCardBrand, formatExpiration } from '@pirouette/shared';
import { supabase } from '@/lib/supabase';
import { useFamilyPaymentMethods, useOpenCustomerPortal } from '@/data/stripe';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Empty } from '@/components/ui/Empty';
import { toast } from '@/lib/toast';

export const Route = createFileRoute('/parent/billing/payment-methods')({
  component: ParentPaymentMethods,
});

function useMyFamilyId() {
  return useQuery({
    queryKey: ['my-family-id'],
    queryFn: async (): Promise<string | null> => {
      const u = await supabase.auth.getUser();
      return (u.data.user?.app_metadata.family_id as string | undefined) ?? null;
    },
  });
}

function ParentPaymentMethods() {
  const myFamily = useMyFamilyId();
  const familyId = myFamily.data ?? undefined;
  const cards = useFamilyPaymentMethods(familyId);
  const portal = useOpenCustomerPortal();

  async function openPortal() {
    try {
      const resp = await portal.mutateAsync({
        return_url: `${window.location.origin}/parent/billing/payment-methods`,
      });
      window.location.href = resp.url;
    } catch (err) {
      toast({
        title: "Couldn't open portal",
        description: err instanceof Error ? err.message : 'Unknown error',
        variant: 'error',
      });
    }
  }

  return (
    <div className="space-y-10">
      <header>
        <p className="tracking-[var(--tracking-wide)] text-[var(--color-ink-muted)] text-[var(--text-sm)] uppercase">
          Billing
        </p>
        <h1 className="mt-2">Payment methods</h1>
        <p className="mt-2 max-w-prose text-[var(--color-ink-muted)]">
          Add, remove, or change your default card through Stripe's secure portal. We never see or
          store your card number — only the last four digits for display.
        </p>
      </header>

      <section aria-labelledby="cards-heading">
        <h2 id="cards-heading" className="sr-only">
          Saved cards
        </h2>
        {cards.isLoading && <p aria-live="polite">Loading cards…</p>}
        {(cards.data?.length ?? 0) === 0 && !cards.isLoading && (
          <Empty
            title="No saved cards"
            description="Click 'Manage in portal' to add one. You can also add a card during checkout."
          />
        )}
        <ul className="mt-2 grid gap-3 sm:grid-cols-2">
          {(cards.data ?? []).map((c) => (
            <li key={c.id} className="surface flex items-center justify-between p-4">
              <div>
                <p className="font-medium">
                  {formatCardBrand(c.brand)} ···· {c.last4}
                </p>
                <p className="text-[var(--color-ink-muted)] text-[var(--text-sm)]">
                  Expires {formatExpiration(c.exp_month, c.exp_year)}
                </p>
              </div>
              {c.is_default && <Badge tone="success">Default</Badge>}
            </li>
          ))}
        </ul>
      </section>

      <Button onClick={() => void openPortal()} disabled={portal.isPending}>
        {portal.isPending ? 'Opening…' : 'Manage in portal'}
      </Button>
    </div>
  );
}
